import {
  GetOfferItemsDocument,
  useDeleteOfferMutation,
  useDuplicateOfferMutation,
  useGetOfferItemsQuery,
  useUpdateOfferMutation,
} from "@/graphql";
import useToast from "@/hooks/useToastNotification";
import { OfferItem, OfferStatus, OfferUpdateInput } from "@/modules/Offers/types/types";
import { computed, ref } from "vue";
import useCampaigns from "./useCampaigns";

const { toastError, toastSuccess } = useToast();

interface Campaign {
  name: string;
  updatedAt: string;
  createdAt: string;
  startDate: string;
  endDate: string;
  offers: OfferItem[] | undefined;
  id: string;
  status: string;
  purchaseLimit: number | null;
}

type Comparator = Campaign | OfferItem | null | undefined;

export function useOffers(appId: string, versionId: string, initialSortOrder = "startDate") {
  const { getCampaigns, campaigns } = useCampaigns(appId, versionId);

  const {
    result,
    loading: queryLoading,
    refetch: refetchOffers,
  } = useGetOfferItemsQuery({ appId, versionId }, { fetchPolicy: "cache-and-network" });

  const noCampaignKey = "noCampaign";
  const campaignsWithOffers = computed(() => {
    const allOffers = result.value?.getOfferItems ?? [];
    const offersByCampaign = new Map<string, typeof allOffers>();

    for (const offer of allOffers) {
      const key = offer?.campaignId || noCampaignKey;
      if (!offersByCampaign.has(key)) {
        offersByCampaign.set(key, []);
      }
      offersByCampaign.get(key)!.push(offer);
    }

    const campaignsWithNoCampaign = [...campaigns.value, { id: noCampaignKey, name: "No campaign" }];
    return campaignsWithNoCampaign.map((campaign) => ({
      ...campaign,
      offers: offersByCampaign.get(campaign.id) ?? [],
    })) as Campaign[];
  });

  const { mutate: updateOfferMutation, loading: updateOfferLoading } = useUpdateOfferMutation({});
  const { mutate: duplicateOfferMutation, loading: duplicateOfferLoading } = useDuplicateOfferMutation({
    update: (cache, data) => {
      cache.updateQuery({ query: GetOfferItemsDocument, variables: { appId, versionId } }, (existingData) => {
        if (data?.data?.duplicateOffer) {
          const newItem = data?.data?.duplicateOffer;
          const existingItems = existingData.getOfferItems;

          return {
            getOfferItems: [...existingItems, newItem],
          };
        }
        return existingData;
      });
    },
  });
  const { mutate: deleteOfferMutation } = useDeleteOfferMutation({});

  const sortOrder = ref(initialSortOrder);
  const selectedStatuses = ref<OfferStatus[]>([]);
  const selectedCampaigns = ref<string[]>([]);

  const offersToDisplay = computed<OfferItem[]>(() => {
    const offers = result.value?.getOfferItems ?? [];
    return offers as OfferItem[];
  });

  function compareDates(a: Comparator, b: Comparator, dateField: string, order: "ASC" | "DESC" = "DESC") {
    const aDate = a?.[dateField];
    const bDate = b?.[dateField];

    // If both are undefined, consider them equal.
    if (aDate === undefined && bDate === undefined) {
      return 0;
    }
    // If only a is undefined, push it to the end.
    if (aDate === undefined) {
      return 1;
    }
    // If only b is undefined, push it to the end.
    if (bDate === undefined) {
      return -1;
    }

    const timeA = new Date(aDate).getTime();
    const timeB = new Date(bDate).getTime();

    if (order === "ASC") {
      return timeA - timeB;
    }

    return timeB - timeA;
  }

  function compareNames(a: Comparator, b: Comparator) {
    const nameA = a?.name || "";
    const nameB = b?.name || "";

    return nameA.localeCompare(nameB);
  }

  const sortFunctions: { [key: string]: (a: Comparator, b: Comparator) => number } = {
    createdAt: (a, b) => compareDates(a, b, "createdAt"),
    startDate: (a, b) => compareDates(a, b, "startDate", "ASC"),
    endDate: (a, b) => compareDates(a, b, "endDate", "ASC"),
    name: (a, b) => compareNames(a, b),
    updatedAt: (a, b) => compareDates(a, b, "updatedAt"),
  };

  const filteredCampaigns = computed(() => {
    if (selectedStatuses.value.length === 0 && selectedCampaigns.value.length === 0) {
      return campaignsWithOffers.value;
    }

    let filteredCampaignsByStatusAndCampaigns = campaignsWithOffers.value;

    if (selectedCampaigns.value.length > 0) {
      filteredCampaignsByStatusAndCampaigns = filteredCampaignsByStatusAndCampaigns.filter((campaign) =>
        selectedCampaigns.value.includes(campaign.id)
      );
      if (selectedCampaigns.value.includes("noCampaign")) {
        filteredCampaignsByStatusAndCampaigns = filteredCampaignsByStatusAndCampaigns.concat(
          campaignsWithOffers.value.filter((campaign) => !campaign.id)
        );
      }
    }

    if (selectedStatuses.value.length > 0) {
      filteredCampaignsByStatusAndCampaigns = filteredCampaignsByStatusAndCampaigns.filter((campaign) =>
        campaign.offers?.some((offer) => {
          if (offer) {
            return selectedStatuses.value.includes(offer?.status);
          }
          return false;
        })
      );

      filteredCampaignsByStatusAndCampaigns = filteredCampaignsByStatusAndCampaigns.map((campaign) => {
        const offers = campaign.offers?.filter((offer) => {
          if (offer) {
            return selectedStatuses.value.includes(offer?.status);
          }
          return false;
        });
        return {
          ...campaign,
          offers,
        };
      });
    }

    return filteredCampaignsByStatusAndCampaigns;
  });

  const sortedCampaigns = computed(() => {
    const campaignsToSort = [...filteredCampaigns.value] as Campaign[];
    const sortFunction = sortFunctions[sortOrder.value] || sortFunctions.updatedAt;

    campaignsToSort.forEach((campaign: Campaign) => {
      if (campaign.offers) {
        campaign.offers = campaign.offers.sort(sortFunction);

        if (campaign.offers[0]) {
          const firstOffer = campaign.offers[0];
          campaign.createdAt = firstOffer.createdAt || "";
          campaign.updatedAt = firstOffer.updatedAt || "";
          campaign.startDate = firstOffer.startDate || "";
          campaign.endDate = firstOffer.endDate || "";
        }
      }
    });

    return campaignsToSort.sort(sortFunction);
  });

  const updateOffer = async (offerId: string, input: OfferUpdateInput) => {
    const response = await updateOfferMutation({
      gameId: appId,
      offerId,
      versionId,
      input,
    });
    if (!response?.data?.updateOfferMutation?.success) {
      toastError({
        detail: response?.data?.updateOfferMutation?.details ?? "An error occurred while updating the offer",
        summary: "Error",
      });
      throw new Error("Error updating offer");
    }

    return response;
  };

  const duplicateOffer = async (offerId: string) => {
    try {
      await duplicateOfferMutation({
        gameId: appId,
        offerId,
        versionId,
      });

      toastSuccess({
        detail: "Offer duplicated successfully!",
        summary: "Success",
        life: 3000,
      });
    } catch (error) {
      toastError({
        detail: "Offer could not be duplicated, please try again",
        summary: "Error",
        life: 3000,
      });
    }
  };

  const deleteOffer = async (offerId: string) => {
    try {
      await deleteOfferMutation({
        gameId: appId,
        offerId,
        versionId,
      });
      toastSuccess({
        detail: "Offer deleted successfully!",
        summary: "Success",
        life: 3000,
      });
    } catch (error) {
      toastError({
        detail: "Offer could not be deleted, please try again",
        summary: "Error",
        life: 3000,
      });
    }
  };

  return {
    offersToDisplay,
    queryLoading,
    refetchOffers,
    updateOffer,
    updateOfferLoading,
    duplicateOffer,
    duplicateOfferLoading,
    deleteOffer,
    sortOrder,
    selectedStatuses,
    selectedCampaigns,
    getCampaigns,
    campaigns,
    sortedCampaigns,
  };
}
